import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeftLong,
  faUsers,
  faFileInvoice,
  faCreditCard,
  faClock,
  faClipboardList,
  faSave,
  faTrash,
  faEnvelope,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { Tabs, Tab, Row, Col, Button, Spinner } from 'react-bootstrap';
import { memo, useState, useEffect } from 'react';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { Container } from '../../../components/ui/Container/Container';
import ModalDialog from '../../../components/ui/ModalDialog/ModalDialog';
import { MemberInformationSection } from '../../../components/member/ClubMemberEditor/MemberInformationSection/MemberInformationSection';
import { MembershipDetailsSection } from '../../../components/member/ClubMemberEditor/MembershipDetailsSection/MembershipDetailsSection';
import { SecondaryAdultMemberSection } from '../../../components/member/ClubMemberEditor/SecondaryAdultMemberSection/SecondaryAdultMemberSection';
import { FamilyMemberChildrenSection } from '../../../components/member/ClubMemberEditor/FamilyMemberChildrenSection/FamilyMemberChildrenSection';
import { AddChildModal } from '../../../components/member/ClubMemberEditor/AddChildModal/AddChildModal';
import { WorkHoursSection } from '../../../components/member/ClubMemberEditor/WorkHoursSection/WorkHoursSection';
import { SetPasswordModal } from '../../../components/user/SetPasswordModal/SetPasswordModal';
import { PaymentSection } from '../../../components/member/ClubMemberEditor/PaymentSection/PaymentSection';
import useClubMemberEditor from './useClubMemberEditor';
import { Helpers } from '../../../utils/helpers';
import { MemberSuffix, MemberTitle } from '../../../utils/constants/enums';
import styles from './ClubMemberEditor.module.css';
import { PaymentSummaryTable } from '../../../components/member/ClubMemberEditor/PaymentSummaryTable/PaymentSummaryTable';
import { useInvoices } from '../../../components/member/ClubMemberEditor/InvoiceSection/useInvoices';
import { usePayments } from '../../../components/member/ClubMemberEditor/PaymentSection/usePayments';
import { InvoiceSection } from '../../../components/member/ClubMemberEditor/InvoiceSection/InvoiceSection';
import { useStoreContext } from '../../../store/store';
import { Permissions } from '../../../utils/constants/permissions';

interface IClubMemberEditorProps {
  clubMemberId?: string;
  setShowEditor?: (show: boolean) => void;
}

// Action Buttons Section Component (moved outside to prevent recreation)
const ActionButtonsSection = memo(function ActionButtonsSection({
  primaryAction,
  cancelAction,
  deleteAction,
  sendEmailAction,
  isSaving,
  isSendingEmail,
  primaryText = 'Save Changes',
  showDelete = false,
  showSendEmail = false
}: {
  primaryAction: () => void;
  cancelAction: () => void;
  deleteAction?: () => void;
  sendEmailAction?: () => void;
  isSaving: boolean;
  isSendingEmail?: boolean;
  primaryText?: string;
  showDelete?: boolean;
  showSendEmail?: boolean;
}) {
  return (
    <div className="d-flex justify-content-between align-items-center mt-4">
      <div className="d-flex gap-2">
        <Button variant="primary" onClick={primaryAction} disabled={isSaving}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          {primaryText}
          {isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
        </Button>
        <Button variant="outline-secondary" onClick={cancelAction} disabled={isSaving}>
          Cancel
        </Button>
        {showDelete && deleteAction && (
          <Button variant="outline-danger" onClick={deleteAction} disabled={isSaving}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete
            {isSaving && <Spinner size="sm" animation="border" className="ms-2" />}
          </Button>
        )}
      </div>
    </div>
  );
});

// Tab Placeholder Component (moved outside to prevent recreation)
const TabPlaceholder = memo(function TabPlaceholder({ tabName }: { tabName: string }) {
  return (
    <div className={styles.placeholderContent}>
      <FontAwesomeIcon
        icon={
          tabName === 'Family'
            ? faUsers
            : tabName === 'Invoices'
              ? faFileInvoice
              : tabName === 'Payments'
                ? faCreditCard
                : faClock
        }
        className={styles.placeholderIcon}
      />
      <div className={styles.placeholderText}>{tabName} functionality coming soon</div>
      <div className={styles.placeholderSubtext}>
        This section will be available in a future update
      </div>
    </div>
  );
});

// Main Tab Content Component (moved outside to prevent recreation)
const MainTabContent = memo(function MainTabContent({
  manager,
  titleOptions,
  suffixOptions,
  isNew,
  canDeleteMembers,
  filteredMemberStatusOptions,
  isDeletedMemberWithoutPermission
}: {
  manager: ReturnType<typeof useClubMemberEditor>;
  titleOptions: Array<{ value: number; label: string }>;
  suffixOptions: Array<{ value: number; label: string }>;
  isNew: boolean;
  canDeleteMembers: boolean;
  filteredMemberStatusOptions: any[];
  isDeletedMemberWithoutPermission: boolean;
}) {
  const hasUserAccount = !isNew && !!manager.clubMember.primaryMember.applicationUserId;

  return (
    <>
      {/* Set Password Button - shown for existing members with user accounts */}
      {hasUserAccount && (
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-secondary" size="sm" onClick={manager.openSetPasswordModal}>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Set Password
          </Button>
        </div>
      )}

      {/* Warning message for deleted members without permission */}
      {isDeletedMemberWithoutPermission && (
        <div className="alert alert-warning mb-3">
          <strong>Read-Only Mode:</strong> This member has a "Delete" status. You do not have
          permission to modify deleted members.
        </div>
      )}

      <Row className={styles.formRow}>
        <Col md={12} className={styles.formColumn}>
          {/* Member Information (Personal + Contact & Address combined) */}
          <MemberInformationSection
            member={manager.clubMember.primaryMember}
            fieldErrors={manager.fieldErrors}
            titleOptions={titleOptions}
            suffixOptions={suffixOptions}
            memberStatusOptions={filteredMemberStatusOptions}
            loadingMemberStatuses={manager.loadingMemberStatuses}
            isPrimary={true}
            disabled={isDeletedMemberWithoutPermission}
            onUpdate={manager.updatePrimaryMemberValue}
          />

          {/* Membership Details & Preferences (Membership + Interests combined) */}
          <MembershipDetailsSection
            clubMember={manager.clubMember}
            fieldErrors={manager.fieldErrors}
            membershipTypes={manager.membershipTypes}
            loadingMembershipTypes={manager.loadingMembershipTypes}
            interests={manager.interests}
            loadingInterests={manager.loadingInterests}
            selectedInterests={manager.selectedInterests}
            isSaving={manager.isSaving}
            disabled={isDeletedMemberWithoutPermission}
            onUpdateClubMember={manager.updateClubMemberValue}
            onUpdateMember={manager.updatePrimaryMemberValue}
            onInterestChange={manager.handleInterestChange}
          />

          {/* Action Buttons */}
          <div className={styles.card}>
            <ActionButtonsSection
              primaryAction={manager.saveChanges}
              cancelAction={manager.cancelChanges}
              deleteAction={manager.openDeleteConfirmationModal}
              sendEmailAction={manager.sendFirstTimeEmail}
              isSaving={manager.isSaving || isDeletedMemberWithoutPermission}
              isSendingEmail={manager.isSendingEmail}
              showDelete={!isNew && canDeleteMembers}
              showSendEmail={!isNew && !!manager.clubMember.primaryMember.applicationUserId}
            />
          </div>
        </Col>
      </Row>
    </>
  );
});

// Family Tab Content Component (moved outside to prevent recreation)
const FamilyTabContent = memo(function FamilyTabContent({
  manager,
  titleOptions,
  suffixOptions,
  filteredMemberStatusOptions,
  isDeletedMemberWithoutPermission
}: {
  manager: ReturnType<typeof useClubMemberEditor>;
  titleOptions: Array<{ value: number; label: string }>;
  suffixOptions: Array<{ value: number; label: string }>;
  filteredMemberStatusOptions: any[];
  isDeletedMemberWithoutPermission: boolean;
}) {
  const hasSecondaryUserAccount = !!manager.clubMember.secondaryMember?.applicationUserId;

  return (
    <>
      {/* Header with Set Password button for secondary member */}
      <div className="d-flex justify-content-between align-items-center mb-4 mx-3">
        <h3 className="mb-0">
          Family Membership for: {manager.clubMember.primaryMember.firstName}{' '}
          {manager.clubMember.primaryMember.lastName}
        </h3>
        {hasSecondaryUserAccount && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={manager.openSecondarySetPasswordModal}>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Set Password (Secondary Member)
          </Button>
        )}
      </div>

      {/* Warning message for deleted members without permission */}
      {isDeletedMemberWithoutPermission && (
        <div className="alert alert-warning mb-3 mx-3">
          <strong>Read-Only Mode:</strong> This member has a "Delete" status. You do not have
          permission to modify deleted members.
        </div>
      )}

      <Row className={styles.formRow}>
        <Col md={12} className={styles.formColumn}>
          {/* Secondary Adult Member Section */}
          <SecondaryAdultMemberSection
            secondaryMember={manager.clubMember.secondaryMember}
            fieldErrors={manager.fieldErrors}
            titleOptions={titleOptions}
            suffixOptions={suffixOptions}
            memberStatusOptions={filteredMemberStatusOptions}
            loadingMemberStatuses={manager.loadingMemberStatuses}
            savingSecondaryMember={manager.savingSecondaryMember || isDeletedMemberWithoutPermission}
            onUpdateSecondaryMember={manager.updateSecondaryMemberValue}
            onCopyAddressFromPrimary={manager.copyAddressFromPrimary}
            onSaveSecondaryMember={manager.saveSecondaryFamilyMember}
            onCancelSecondaryMember={manager.cancelSecondaryFamilyMember}
            onDeleteSecondaryMember={undefined} // You may need to add a delete function
          />
        </Col>
      </Row>

      {/* Family Member's Children Section */}
      <FamilyMemberChildrenSection
        familyMemberChildren={manager.familyMemberChildren}
        loadingChildren={manager.loadingChildren}
        onAddChild={manager.openAddChildModal}
        onEditChild={manager.enableEditChildMode}
      />
    </>
  );
});

// Work Hours Tab Content Component (moved outside to prevent recreation)
const WorkHoursTabContent = memo(function WorkHoursTabContent({
  manager
}: {
  manager: ReturnType<typeof useClubMemberEditor>;
}) {
  return (
    <Row className={styles.formRow}>
      <Col md={12} className={styles.formColumn}>
        <WorkHoursSection
          clubMemberId={manager.clubMember.id}
          committees={manager.committees}
          loadingCommittees={manager.loadingCommittees}
          fieldErrors={manager.fieldErrors}
          setFieldErrors={manager.setFieldErrors}
          memberFirstName={manager.clubMember.primaryMember.firstName}
          memberLastName={manager.clubMember.primaryMember.lastName}
          showHeader={true}
        />
      </Col>
    </Row>
  );
});

// Payment Tab Content Component (moved outside to prevent recreation)
const PaymentTabContent = memo(function PaymentTabContent({
  manager,
  isActive
}: {
  manager: ReturnType<typeof useClubMemberEditor>;
  isActive: boolean;
}) {
  const paymentsHook = usePayments({
    clubMemberId: manager.clubMember.id,
    fieldErrors: manager.fieldErrors,
    setFieldErrors: manager.setFieldErrors
  });

  const invoicesHook = useInvoices({
    clubMemberId: manager.clubMember.id,
    fieldErrors: manager.fieldErrors,
    setFieldErrors: manager.setFieldErrors
  });

  // Refetch invoices when the tab becomes active
  useEffect(() => {
    if (isActive && manager.clubMember.id && manager.clubMember.id !== 'new') {
      void invoicesHook.refetchInvoices(manager.clubMember.id);
    }
  }, [isActive]);

  const paymentSummary = paymentsHook.getPaymentSummary(invoicesHook.invoices);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-4 mx-3">
        <h3 className="mb-0">
          Payments for: {manager.clubMember.primaryMember.firstName}{' '}
          {manager.clubMember.primaryMember.lastName}
        </h3>
        <PaymentSummaryTable paymentSummary={paymentSummary} />
      </div>

      <Row className={styles.formRow}>
        <Col md={12} className={styles.formColumn}>
          <PaymentSection paymentsHook={paymentsHook} />
        </Col>
      </Row>
    </>
  );
});

// Invoice Tab Content Component (moved outside to prevent recreation)
const InvoiceTabContent = memo(function InvoiceTabContent({
  manager
}: {
  manager: ReturnType<typeof useClubMemberEditor>;
}) {
  return (
    <Row className={styles.formRow}>
      <Col md={12} className={styles.formColumn}>
        <InvoiceSection
          clubMemberId={manager.clubMember.id}
          fieldErrors={manager.fieldErrors}
          setFieldErrors={manager.setFieldErrors}
          memberFirstName={manager.clubMember.primaryMember.firstName}
          memberLastName={manager.clubMember.primaryMember.lastName}
          clubMember={manager.clubMember}
          showHeader={true}
        />
      </Col>
    </Row>
  );
});

export const ClubMemberEditor = ({ clubMemberId = '' }: IClubMemberEditorProps) => {
  const manager = useClubMemberEditor(clubMemberId);
  const ToastContainer = manager.ToastContainer;
  const { user } = useStoreContext();
  const isNew =
    Helpers.isNullOrWhiteSpace(manager.clubMember.id) || manager.clubMember.id === 'new';
  const [activeTab, setActiveTab] = useState<string>('main');

  // Check if user has permission to delete members
  const canDeleteMembers =
    user.permissions.includes(Permissions.MEMBER_DELETE) ||
    user.permissions.includes(Permissions.SYSADMIN);

  // Find the DELETE status from available options
  const deleteStatus = manager.memberStatusOptions.find((status) => status.codeValue === 'DELETE');
  const deleteStatusId = deleteStatus?.id;

  // Check if member has DELETE status and user doesn't have permission to modify
  const isDeletedMemberWithoutPermission =
    !isNew &&
    !canDeleteMembers &&
    deleteStatusId !== undefined &&
    manager.clubMember.primaryMember?.memberStatusId === deleteStatusId;

  // Filter member status options - exclude DELETE if user doesn't have permission
  // BUT include DELETE if the member currently has that status (so it can be displayed)
  const filteredMemberStatusOptions = canDeleteMembers
    ? manager.memberStatusOptions
    : manager.memberStatusOptions.filter(
        (status) =>
          status.codeValue !== 'DELETE' ||
          (isDeletedMemberWithoutPermission && status.id === deleteStatusId)
      );

  // Reset to main tab when opening a different member
  useEffect(() => {
    setActiveTab('main');
  }, [clubMemberId]);

  // Handle tab switching after discarding changes
  useEffect(() => {
    if (
      manager.dialogAction === 'tabSwitch' &&
      !manager.showUnsavedChangesDialog &&
      !manager.isDirty
    ) {
      const targetTab = manager.targetTab;
      if (typeof targetTab === 'string') {
        setActiveTab(targetTab);
      }
      manager.setDialogAction(null);
    }
  }, [
    manager.dialogAction,
    manager.showUnsavedChangesDialog,
    manager.isDirty,
    manager.targetTab,
    manager.setDialogAction
  ]);

  // Check if family members are allowed
  // Allow if: membership type supports family OR existing secondary/children exist
  const allowFamilyMembers =
    !isNew &&
    // Allow if membership type supports family members
    (manager.clubMember.membershipType?.familyMembers === true ||
      // OR if secondary adult already exists
      (manager.clubMember.secondaryMember !== null &&
        manager.clubMember.secondaryMember !== undefined) ||
      // OR if children already exist
      (manager.familyMemberChildren && manager.familyMemberChildren.length > 0));

  // Handle tab selection - prevent selecting disabled tabs and check for unsaved changes
  const handleTabSelect = (key: string | null) => {
    if (key === 'family' && !allowFamilyMembers) {
      return; // Don't allow selecting the family tab if not allowed
    }

    // Check for unsaved changes before switching tabs
    if (manager.isDirty && key !== activeTab) {
      // Store the target tab and show unsaved changes dialog
      const setDialogAction = manager.setDialogAction;
      const setTargetTab = manager.setTargetTab;
      const setShowUnsavedChangesDialog = manager.setShowUnsavedChangesDialog;
      if (typeof setDialogAction === 'function') {
        setDialogAction('tabSwitch');
      }
      if (typeof setTargetTab === 'function') {
        setTargetTab(key || 'main');
      }
      if (typeof setShowUnsavedChangesDialog === 'function') {
        setShowUnsavedChangesDialog(true);
      }
      return;
    }

    setActiveTab(key || 'main');
  };

  if (!clubMemberId) return null;

  // Dropdown options - filter out None values since we use manual empty options
  const titleOptions: Array<{ value: number; label: string }> = Object.entries(MemberTitle)
    .filter(([label, value]) => typeof value === 'number' && label !== 'None')
    .map(([label, value]) => ({ value: value as number, label }));

  const suffixOptions: Array<{ value: number; label: string }> = Object.entries(MemberSuffix)
    .filter(([label, value]) => typeof value === 'number' && label !== 'None')
    .map(([label, value]) => ({ value: value as number, label }));

  return (
    <>
      <ToastContainer />
      <ModalDialog
        showDialog={manager.showUnsavedChangesDialog}
        heading="Unsaved Changes"
        message={
          manager.dialogAction === 'back'
            ? 'You have unsaved changes. Do you want to discard your changes and return to the member list?'
            : manager.dialogAction === 'tabSwitch'
              ? 'You have unsaved changes. Do you want to discard your changes and switch tabs?'
              : manager.dialogAction === 'navigation'
                ? 'You have unsaved changes. Do you want to discard your changes and navigate away?'
                : 'You have unsaved changes. Do you want to discard your changes and reset the form?'
        }
        okCallback={() => {
          const discardFunction = manager.discardUnsavedChanges;
          if (typeof discardFunction === 'function') {
            discardFunction();
          }
        }}
        okButtonText="Yes, discard changes"
        cancelCallback={() => {
          const closeFunction = manager.closeUnsavedChangesDialog;
          if (typeof closeFunction === 'function') {
            closeFunction();
          }
        }}
        cancelButtonText="No, keep editing"
      />
      <ModalDialog
        showDialog={manager.showDeleteConfirmationModal}
        heading="Set Member Status to Delete"
        message={`Are you sure you want to change the status of ${manager.clubMember.primaryMember.firstName} ${manager.clubMember.primaryMember.lastName} to "Delete"?`}
        okCallback={() => {
          manager.closeDeleteConfirmationModal();
          void manager.deleteMember();
        }}
        okButtonText="Yes, set status to Delete"
        cancelCallback={() => {
          manager.closeDeleteConfirmationModal();
        }}
        cancelButtonText="Cancel"
      />
      <LoadingContainer isLoading={manager.loadingClubMember}>
        <Container className={styles.editorContainer}>
          {/* Header Section */}
          <div className={styles.header}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const backFunction = manager.back;
                if (typeof backFunction === 'function') {
                  backFunction();
                }
              }}
              className={styles.backLink}>
              <FontAwesomeIcon icon={faArrowLeftLong} className="me-2" />
              Return to member list
            </a>
            <h2 className={styles.title}>{isNew ? 'Create' : 'Edit'} Club Member</h2>
          </div>

          {/* Tabs Navigation */}
          <div className={styles.tabsContainer}>
            <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3">
              <Tab
                eventKey="main"
                title={
                  <span className={styles.customTab}>
                    <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                    Main
                  </span>
                }>
                <div className={styles.tabContent}>
                  <MainTabContent
                    manager={manager}
                    titleOptions={titleOptions}
                    suffixOptions={suffixOptions}
                    isNew={isNew}
                    canDeleteMembers={canDeleteMembers}
                    filteredMemberStatusOptions={filteredMemberStatusOptions}
                    isDeletedMemberWithoutPermission={isDeletedMemberWithoutPermission}
                  />
                </div>
              </Tab>
              <Tab
                eventKey="family"
                title={
                  <span
                    className={`${styles.customTab} ${!allowFamilyMembers ? styles.disabledTab : ''}`}>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Family
                    {!allowFamilyMembers && (
                      <span className={styles.tabDisabledMsg}>
                        {isNew ? ' (Available after save)' : ' (Not available for this membership)'}
                      </span>
                    )}
                  </span>
                }
                disabled={!allowFamilyMembers}>
                <div className={styles.tabContent}>
                  {allowFamilyMembers ? (
                    <FamilyTabContent
                      manager={manager}
                      titleOptions={titleOptions}
                      suffixOptions={suffixOptions}
                      filteredMemberStatusOptions={filteredMemberStatusOptions}
                      isDeletedMemberWithoutPermission={isDeletedMemberWithoutPermission}
                    />
                  ) : (
                    <TabPlaceholder tabName="Family" />
                  )}
                </div>
              </Tab>
              <Tab
                eventKey="invoices"
                title={
                  <span className={styles.customTab}>
                    <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                    Invoices
                  </span>
                }>
                <div className={styles.tabContent}>
                  <InvoiceTabContent manager={manager} />
                </div>
              </Tab>
              <Tab
                eventKey="payments"
                title={
                  <span className={styles.customTab}>
                    <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                    Payments
                  </span>
                }>
                <div className={styles.tabContent}>
                  <PaymentTabContent manager={manager} isActive={activeTab === 'payments'} />
                </div>
              </Tab>
              <Tab
                eventKey="workhours"
                title={
                  <span className={styles.customTab}>
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Work Hours
                  </span>
                }>
                <div className={styles.tabContent}>
                  <WorkHoursTabContent manager={manager} />
                </div>
              </Tab>
            </Tabs>
          </div>
        </Container>
      </LoadingContainer>

      <AddChildModal
        show={manager.showAddChildModal}
        onHide={manager.closeAddChildModal}
        childFormData={manager.childFormData}
        childFormErrors={manager.childFormErrors}
        addingChild={manager.addingChild}
        childFormMode={manager.childFormMode}
        showDeleteChildConfirmation={manager.showDeleteChildConfirmation}
        deletingChild={manager.deletingChild}
        onUpdateChildFormData={manager.updateChildFormData}
        onAddChild={manager.addChild}
        onShowDeleteConfirmation={manager.showDeleteChildConfirmationDialog}
        onHideDeleteConfirmation={manager.hideDeleteChildConfirmationDialog}
        onDeleteChild={manager.deleteChild}
      />

      <SetPasswordModal
        visible={manager.showSetPasswordModal}
        userId={manager.clubMember.primaryMember.applicationUserId || ''}
        onSuccess={manager.onPasswordSetSuccess}
        onClose={manager.closeSetPasswordModal}
      />

      <SetPasswordModal
        visible={manager.showSecondarySetPasswordModal}
        userId={manager.clubMember.secondaryMember?.applicationUserId || ''}
        onSuccess={manager.onSecondaryPasswordSetSuccess}
        onClose={manager.closeSecondarySetPasswordModal}
      />

      <ModalDialog
        showDialog={manager.showEmailReuseConfirmDialog}
        heading="Email Account Already Exists"
        message={
          manager.emailConflictInfo ? (
            <>
              <div className="alert alert-warning">
                {manager.emailConflictInfo.message ||
                  `This email (${manager.emailConflictInfo.email}) is already linked to ${manager.emailConflictInfo.memberName}.`}
              </div>
              <p>Do you want to continue and transfer this login to this member record?</p>
            </>
          ) : (
            'Confirm account transfer?'
          )
        }
        okCallback={manager.confirmEmailReuse}
        okButtonText="Yes, Transfer Account"
        cancelCallback={manager.cancelEmailReuse}
        cancelButtonText="Cancel"
      />
    </>
  );
};
